import { Button, Modal } from "@material-ui/core";
import KnobIcon from "@material-ui/icons/DescriptionOutlined";
import { ReactComponent as CancelBtn } from "assets/Cancel.svg";
import clsx from "clsx";
import { exists, t } from "i18next";
import lodash from "lodash";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useThemeClassName from "qpr-base/themehooks/useThemeClassname";
import Loader from "qpr-base/ui/components/Loader";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import Scrollbar from "react-scrollbars-custom";
import { KPIDetailsMetadataContainer } from "./KPIDetailsMetadataContainer";
export const useStylesForMetadata = makeThemedStyles(
  "KPIDetailsContent.Metadata",
  (theme) => ({
    HeaderRow: {},

    Icon: {},
    PropsArea: {},
    Header: {
      fontFamily: "Ample",
    },
    Name: {},
    Value: {},
    IconContainer: {},
    More: {
      paddingInlineStart: "12px",
    },
    MoreLabel: {
      fontSize: "1.6em",
    },
  })
);

export const useStylesForMetadataPopup = (rtl?: boolean) =>
  makeThemedStyles("KPIDetailsContent.ModalMetadata", (theme) => ({
    Modal: {},
    ModalPaper: {},
    ModalContainer: {},

    ModalContainer2: {
      display: "flex",
      height: "100%",
      flexDirection: "column",
      padding: "40px 20px 20px 20px",
    },
    HeaderRow: {
      width: "100%",
    },
    HeaderRow2: {
      marginTop: "-30px",
      marginBottom: "1.428em",
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    },
    HeaderLeft: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    IconClose: {
      cursor: "pointer",
      backgroundPosition: "0px 0px",
      backgroundRepeat: "no-repeat",
    },
    Icon: {},
    PropsArea: {
      fontSize: "16px",
      "& table td:first-child": {
        width: "15%",
        height: "2em",
      },

      "& table td:last-child": {
        width: "85%",
        height: "2em",
        whiteSpace: "pre-wrap",
      },
    },
    Prop: {},
    Header: {
      color: "rgb(17,94,103)",
      marginLeft: rtl ? undefined : "1.142em",
      marginRight: rtl ? "1.142em" : undefined,
    },
    Name: {},
    Value: {},
    IconContainer: {},
    More: {},
    rowText: {
      fontSize: "0.875em",
      lineHeight: "1.428em",
      color: "rgba(76, 81, 87, 1)",
      fontWeight: "bold",
      padding: rtl
        ? "0.571em 0 0.571em 1.1428em"
        : "0.571em 1.1428em 0.571em 0",
    },
    rowValue: {
      color: "rgba(76, 81, 87, 1)",
      fontWeight: "unset",
      width: "100%",
      display: "table",
      tableLayout: "fixed",
      overflow: "auto",
      wordWrap: "break-word",
    },
    buttons: {
      display: "flex",
      alignItems: "center",
    },
    editBtn: {
      [rtl ? "marginLeft" : "marginRight"]: "40px",
      textTransform: "none",
      fontSize: "1.828em",
      color: "rgba(76, 81, 87, 1)",
      "& svg": {
        color: "rgb(0, 169, 163)",
        width: "20px",
        height: "20px",
        [rtl ? "marginLeft" : "marginRight"]: "8px",
      },
      "&:hover": {
        background: "transparent",
      },
    },
  }))();

type IList = {
  keys: string[];
  data: { [key: string]: string };
  classes: [string, string];
  sortBy: string[];
};

const dateFormatter = (str: string, key: string) => {
  const date = new Date(str);
  if (
    !key.toLowerCase().includes("date") ||
    date.toString() === "Invalid Date"
  ) {
    return str;
  }
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};
export function List(props: IList) {
  const { keys, data, classes, sortBy } = props;
  const [column_1, column_2] = classes;
  const translatedKeys = keys.filter((key) => exists(key));
  const keysWithTranslation =
    sortBy.length === 0
      ? translatedKeys
      : lodash.sortBy(translatedKeys, (key: string) => {
          const index = sortBy.indexOf(key);
          return index !== -1 ? index : keys.length;
        });
  if (keysWithTranslation.length === 0) return null;
  return (
    <>
      {keysWithTranslation.map((key: string) => {
        if (key.includes("Project Budget")) {
          data[key] = Number(data[key]).toLocaleString();
        }
        return (
          <tr
            key={key}
            style={{ borderBottom: "0.0625em solid rgba(151, 151, 151, 0.16)" }}
          >
            <td className={column_1}>{t(key)}</td>
            <td className={clsx(column_1, column_2)}> {t(data[key])}</td>
          </tr>
        );
      })}
    </>
  );
}

const MetaView = (props: {
  componentData: {
    title?: string;
    useKnob?: boolean;
    query: string;
    attributes: string;
    displayedItems?: Array<string>;
  };
}) => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const metaStyles = useStylesForMetadata();
  const { language: lang, rtl } = useAppContext((state) => state);
  const themeClass = useThemeClassName();
  const modalStyles = useStylesForMetadataPopup(rtl);

  const displayedMetadata = (props.componentData.displayedItems || []).map(
    (item) => item.toLowerCase()
  );
  const { data, isLoading, isError } = useMeaQuery<Array<any>>(
    props.componentData.query,
    props.componentData.attributes
  );
  if (isLoading || !data) {
    return <Loader />;
  }
  if (isError || !data || !data?.data[0]) {
    return <div>Error loading metadata</div>;
  }
  // TODO: there is a small bug in the server JSON, newlines are not escaped correctly
  const str = (data?.data[0]?.translations || "").replace(/\n/g, "\\n");
  const metaData = JSON.parse(str || "null");
  if (!metaData || !metaData["Localized-Text-Attributes"]) {
    // return <div>Error loading metadata, no localized attributes</div>;
    return <></>;
  }
  const metaValues =
    metaData["Localized-Text-Attributes"][lang] ||
    metaData["Localized-Text-Attributes"][lang.substring(0, 2)];
  const sortBy =
    metaData["Localized-Text-Attributes"]["Attributes-Order"] || [];

  const getMetaDataItems = () => {
    const items: { [key: string]: any } = {};
    Object.keys(metaData).forEach((key) => {
      if (
        ![
          "Foreign-Keys",
          "Localized-Text-Attributes",
          "Attributes-Order",
        ].includes(key)
      ) {
        const data = dateFormatter(metaData[key], key);
        items[key] = data;
      }
    });
    const foreignKey = metaData["Foreign-Keys"] || {};
    return { ...items, ...foreignKey };
  };

  const metaDataForeignValues = getMetaDataItems();
  if (!metaValues) {
    return <div>Invalid language {lang}</div>;
  }
  const metadataKeys = Object.keys(metaValues);

  const metaDataForeignKeys = Object.keys(metaDataForeignValues);
  return (
    <>
      <KPIDetailsMetadataContainer>
        {!props.componentData.useKnob && (
          <div className={metaStyles.HeaderRow}>
            <div className={metaStyles.IconContainer}>
              <KnobIcon className={metaStyles.Icon} />
            </div>
            <div
              className={clsx(metaStyles.Header, "DinNextLTArabic")}
              style={{ marginRight: rtl ? "16px" : "" }}
            >
              {props.componentData.title}
            </div>
          </div>
        )}

        <div className={metaStyles.PropsArea}>
          {metadataKeys
            .filter((key) =>
              displayedMetadata.includes(
                key.toLowerCase().replace("kpi ", "").trim()
              )
            )
            .map((key) => {
              return (
                <div key={key}>
                  {" "}
                  <div className={metaStyles.Name}>{t(key)}</div>
                  <div className={metaStyles.Value}>{metaValues[key]}</div>
                </div>
              );
            })}
          <Button
            className={metaStyles.More}
            classes={{ label: metaStyles.MoreLabel }}
            onClick={() => {
              setModalOpen(true);
            }}
          >
            {(props.componentData.useKnob && <KnobIcon fontSize="large" />) ||
              t("See More")}
          </Button>
        </div>
      </KPIDetailsMetadataContainer>
      <Modal
        open={isModalOpen}
        dir={rtl ? "rtl" : "ltr"}
        style={{ zIndex: 99999 }}
        className={`${modalStyles.Modal} ${themeClass}`}
        onClose={() => {
          setModalOpen(false);
        }}
      >
        <div className={modalStyles.ModalPaper}>
          <div className={modalStyles.ModalContainer2}>
            <div className={modalStyles.HeaderRow2}>
              <div className={modalStyles.HeaderLeft}>
                <div
                  role="button"
                  tabIndex={0}
                  className={modalStyles.IconContainer}
                  onKeyDown={() => {}}
                  onClick={() => {
                    setModalOpen(false);
                  }}
                >
                  {props.componentData.useKnob ? (
                    <KnobIcon fontSize="large" className={modalStyles.Icon} />
                  ) : (
                    <KnobIcon
                      style={{
                        width: "1.333em",
                        transform: "scale(1.5)",
                        height: "1.666em",
                      }}
                      className={modalStyles.Icon}
                    />
                  )}
                </div>
                <div className={clsx(modalStyles.Header, "DinNextLTArabic")}>
                  {props.componentData.title}
                </div>
              </div>
              <CancelBtn
                className={modalStyles.IconClose}
                onClick={() => setModalOpen(false)}
              />
            </div>
            <div style={{ height: "100%" }}>
              <Scrollbar
                style={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <div className={modalStyles.PropsArea}>
                  <table width="100%" style={{ borderCollapse: "collapse" }}>
                    <tbody>
                      <List
                        keys={[...metaDataForeignKeys, ...metadataKeys]}
                        data={{ ...metaValues, ...metaDataForeignValues }}
                        sortBy={sortBy}
                        classes={[modalStyles.rowText, modalStyles.rowValue]}
                      />
                    </tbody>
                  </table>
                </div>
              </Scrollbar>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MetaView;
